import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import PaymentRequiredModal from './features/payment/payment-required-modal';
import { de, enUS } from 'date-fns/locale';
import GlobalScrollbarStyles from 'features/ui/app-layout/custom-scrollbar';
import { DynamicLandingRedirect } from 'features/ui/app-layout/feature-gated-route';
import ErrorBoundary from 'features/ui/error-boundary/error-boundary';
import useCheckFrontendVersion from 'hooks/use-check-frontend-version';
import { useProfileLanguage } from 'hooks/use-profile-language';
import { useUserData } from 'hooks/use-user-data';
import i18n from 'i18n';
import RedirectToLogin from 'features/ui/app-layout/redirect-to-login';
import RedirectToEntry from 'features/ui/app-layout/redirect-to-entry';
import { getCoPilotChildRoutes } from 'pages/co-pilot/co-pilot-route-children';
import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Navigate, RouterProvider, createBrowserRouter } from 'react-router';
import { ReactFlowProvider } from 'reactflow';
import theme from 'theme';
import { TssCacheProvider } from 'tss-react';
import config from './config/default';
import { ProductGroups, ProfileTypes, Routes as RoutesEnum } from './enums';
import PublicChatRoute from './pages/public-chat';

// Pages load on demand: the entry page (/welcome) only ships what it renders, the rest of the
// app follows after sign-in. One Suspense around the router shows a spinner while a page loads.
const UserLanding = lazy(() => import('features/account/user/user-landing'));
const CallTranscriptUI = lazy(() => import('features/co-pilot/call-transcripts/call-transcripts'));
const LinkedinAccountExternal = lazy(
  () => import('features/co-pilot/linkedin-accounts/linkedin-account-external')
);
const PrivateRoute = lazy(() => import('features/ui/app-layout/private-route'));
const AdminRoute = lazy(() => import('features/ui/app-layout/admin-route'));
const AccountManagement = lazy(() => import('pages/account-management'));
const Administration = lazy(() => import('pages/admin'));
const AdminLinkedinSessionMonitorPage = lazy(() => import('pages/admin-linkedin-session-monitor'));
const AdminCampaignContactTimelinePage = lazy(
  () => import('pages/admin-campaign-contact-timeline')
);
const AdminFeedbackTicketsPage = lazy(() => import('pages/admin-feedback-tickets'));
const AdminEngagementPage = lazy(() => import('pages/admin-engagement'));
const NotificationsPage = lazy(() => import('pages/notifications'));
const FeedbackTicketPage = lazy(() => import('pages/feedback-ticket'));
const AIChatPage = lazy(() => import('pages/ai-chat'));
const AdminMonitoring = lazy(() => import('pages/admin-monitoring'));
const AdminQueueManagement = lazy(() => import('pages/admin-queue-management'));
const AugmentedWriting = lazy(() => import('pages/augmented-writing'));
const AugmentedWritingComplete = lazy(() => import('pages/augmented-writing-complete'));
const CoPilotRootLayout = lazy(() => import('pages/co-pilot/co-pilot-root-layout'));
const ContactDetails = lazy(() => import('pages/contact-details'));
const ContactManagement = lazy(() => import('pages/contact-management'));
const ExtensionInstallationSuccess = lazy(() => import('pages/extension-installation-success'));
const FeatureOverview = lazy(() => import('pages/feature-overview'));
const HrBooster = lazy(() => import('pages/hr-booster'));
const HrBoosterTextGenerationNew = lazy(() => import('pages/hr-booster-text-generation'));
const LeadBooster = lazy(() => import('pages/lead-booster'));
const LeadBoosterTextGeneration = lazy(() => import('pages/lead-booster-text-generation'));
const LeadListResearchGenerate = lazy(() => import('pages/lead-list-research-generate'));
const LeadListStorage = lazy(() => import('pages/lead-list-storage'));
const LeadListStorageDetail = lazy(() => import('pages/lead-list-storage-detail'));
const LeadManageQuestionnaires = lazy(() => import('pages/lead-manage-questionnaires'));
const LeadQuestionnaire = lazy(() => import('pages/lead-questionnaire'));
const LeadQuestionnaireDetails = lazy(() => import('pages/lead-questionnaire-details'));
const LinkedinProfileFinder = lazy(() => import('pages/linkedin-profile-finder'));
const MotivEManageSurveys = lazy(() => import('pages/motive-manage-surveys'));
const MotiveSurvey = lazy(() => import('pages/motive-survey'));
const MotiveTests = lazy(() => import('pages/motive-tests'));
const MotiveWebReportPage = lazy(() => import('pages/motive-web-report'));
const PageNotFound = lazy(() => import('pages/page-not-found'));
const Payment = lazy(() => import('pages/payment'));
const Pipeline = lazy(() => import('pages/pipeline'));
const ProfileGenerator = lazy(() => import('pages/profile-generator'));
const PromptManagement = lazy(() => import('pages/prompt-management'));
const ResetPasswordEnterNew = lazy(() => import('pages/reset-password-enter-new'));
const ResetPasswordRequest = lazy(() => import('pages/reset-password-request'));
const GoogleCallback = lazy(() => import('pages/google-callback'));
const SignUpSuccess = lazy(() => import('pages/sign-up-success'));
const StatusMessageManagement = lazy(() => import('pages/status-message-management'));
const SudoSignIn = lazy(() => import('pages/sudo-sign-in'));
const UseCaseStore = lazy(() => import('pages/use-case-store'));
const WebmeticsLanding = lazy(() => import('./pages/apps-management'));
const OauthSuccess = lazy(() => import('./pages/o-auth-success'));

const muiCache = createCache({
  key: 'mui',
  prepend: true,
});

const tssCache = createCache({
  key: 'tss',
});

declare global {
  interface Window {
    _rwq: any;
    Rewardful: any;
  }
}

const PageLoading: React.FC = () => (
  <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <CircularProgress />
  </Box>
);

const App: React.FC = () => {
  const { isAuthenticated } = useUserData();
  const authenticated = isAuthenticated();

  const queryParams = new URLSearchParams(window.location.search);
  const email = queryParams.get('optOutEmail');

  const adapterLocale = i18n.languages[0] === 'de' ? de : enUS;

  useCheckFrontendVersion();

  // Stripe only matters on the payment pages. Its script (js.stripe.com) used to load on import,
  // on every page including the entry page; now it follows once the page is idle. <Elements>
  // accepts null first, and useStripe() callers already handle a not-yet-loaded Stripe.
  const [stripePromise, setStripePromise] = useState<ReturnType<typeof loadStripe> | null>(null);
  useEffect(() => {
    const timer = window.setTimeout(() => setStripePromise(loadStripe(config.stripe_pk)), 3000);
    return () => window.clearTimeout(timer);
  }, []);

  // Session-Restore: UI-Sprache aus dem Nutzerprofil übernehmen.
  useProfileLanguage();

  useEffect(() => {
    (function (w: any, r: any) {
      w._rwq = r;
      w[r] =
        w[r] ||
        function (...args: any[]) {
          (w[r].q = w[r].q || []).push(args);
        };
    })(window, 'rewardful');
    const scriptOne = document.createElement('script');
    scriptOne.async = true;
    scriptOne.src = 'https://r.wdfl.co/rw.js';
    scriptOne.setAttribute('data-rewardful', 'dcceb5');

    document.head.appendChild(scriptOne);
  }, []);

  const router = createBrowserRouter(
    [
      {
        path: '/',
        element: authenticated ? (
          <PrivateRoute>
            <DynamicLandingRedirect />
          </PrivateRoute>
        ) : (
          <ErrorBoundary>
            <PublicChatRoute />
          </ErrorBoundary>
        ),
      },
      {
        path: RoutesEnum.PUBLIC_CHAT,
        element: (
          <ErrorBoundary>
            <PublicChatRoute />
          </ErrorBoundary>
        ),
      },
      {
        path: '/blank',
        element: (
          <PrivateRoute>
            <div />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.LANDING,
        element: (
          <Navigate
            to={{
              pathname: !authenticated ? RoutesEnum.PUBLIC_CHAT : RoutesEnum.AI_CHAT,
              search: email ? `?optOutEmail=${email}` : '',
            }}
            replace={false}
          />
        ),
      },
      {
        path: RoutesEnum.SIGNIN,
        element: <RedirectToEntry tab="login" />,
      },
      {
        path: RoutesEnum.ADMIN_SIGN_IN,
        element: (
          <ErrorBoundary>
            <UserLanding authenticated={authenticated}>
              <SudoSignIn />
            </UserLanding>
          </ErrorBoundary>
        ),
      },
      {
        path: RoutesEnum.SIGNUP,
        element: <RedirectToEntry tab="signup" />,
      },
      {
        path: RoutesEnum.GOOGLE_CALLBACK,
        element: (
          <ErrorBoundary>
            <UserLanding authenticated={authenticated}>
              <GoogleCallback />
            </UserLanding>
          </ErrorBoundary>
        ),
      },
      {
        path: RoutesEnum.SIGNUP_SUCCESS,
        element: (
          <ErrorBoundary>
            <UserLanding authenticated={authenticated}>
              <SignUpSuccess />
            </UserLanding>
          </ErrorBoundary>
        ),
      },
      {
        path: RoutesEnum.FORGET_PASSWORD,
        element: (
          <ErrorBoundary>
            <UserLanding authenticated={authenticated}>
              <ResetPasswordRequest />
            </UserLanding>
          </ErrorBoundary>
        ),
      },
      {
        path: RoutesEnum.RESET_PASSWORD,
        element: (
          <ErrorBoundary>
            <UserLanding authenticated={authenticated}>
              <ResetPasswordEnterNew />
            </UserLanding>
          </ErrorBoundary>
        ),
      },
      {
        path: RoutesEnum.INSTALL_EXTENSION_SUCCESS,
        element: (
          <ErrorBoundary>
            <ExtensionInstallationSuccess />
          </ErrorBoundary>
        ),
      },
      {
        path: `${RoutesEnum.SURVEY}/:id`,
        element: (
          <ErrorBoundary>
            <MotiveSurvey />
          </ErrorBoundary>
        ),
      },
      {
        path: `${RoutesEnum.MOTIVE_WEB_REPORT}/:token`,
        element: (
          <ErrorBoundary>
            <MotiveWebReportPage />
          </ErrorBoundary>
        ),
      },
      {
        path: RoutesEnum.SURVEY_OVERVIEW,
        element: (
          <PrivateRoute>
            <MotivEManageSurveys />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.LEAD_CREATION,
        element: (
          <PrivateRoute>
            <LeadManageQuestionnaires />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.LEAD_CREATION_DETAILS,
        element: (
          <PrivateRoute>
            <LeadQuestionnaireDetails />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.LEAD_QUESTIONNAIRE,
        element: (
          <ErrorBoundary>
            <LeadQuestionnaire />
          </ErrorBoundary>
        ),
      },
      {
        path: RoutesEnum.OAUTH_MICROSOFT_SUCCESS,
        element: (
          <ErrorBoundary>
            <OauthSuccess />
          </ErrorBoundary>
        ),
      },
      {
        path: RoutesEnum.OAUTH_GMAIL_SUCCESS,
        element: (
          <ErrorBoundary>
            <OauthSuccess />
          </ErrorBoundary>
        ),
      },
      {
        path: RoutesEnum.ADMINISTRATION,
        element: (
          <AdminRoute>
            <Administration />
          </AdminRoute>
        ),
      },
      {
        path: RoutesEnum.ADMIN_MONITORING,
        element: (
          <AdminRoute>
            <AdminMonitoring />
          </AdminRoute>
        ),
      },
      {
        path: RoutesEnum.ADMIN_QUEUE_MANAGEMENT,
        element: (
          <AdminRoute>
            <AdminQueueManagement />
          </AdminRoute>
        ),
      },
      {
        path: RoutesEnum.ADMIN_LINKEDIN_SESSION_MONITOR,
        element: (
          <AdminRoute>
            <AdminLinkedinSessionMonitorPage />
          </AdminRoute>
        ),
      },
      {
        path: RoutesEnum.ADMIN_CAMPAIGN_CONTACT_TIMELINE,
        element: (
          <AdminRoute>
            <AdminCampaignContactTimelinePage />
          </AdminRoute>
        ),
      },
      {
        path: RoutesEnum.ADMIN_FEEDBACK_TICKETS,
        element: (
          <AdminRoute>
            <AdminFeedbackTicketsPage />
          </AdminRoute>
        ),
      },
      {
        path: `${RoutesEnum.ADMIN_ENGAGEMENT}/:tab?`,
        element: (
          <AdminRoute>
            <AdminEngagementPage />
          </AdminRoute>
        ),
      },
      {
        path: RoutesEnum.NOTIFICATIONS,
        element: (
          <PrivateRoute>
            <NotificationsPage />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.PROMPT_MANAGEMENT,
        element: (
          <AdminRoute>
            <PromptManagement />
          </AdminRoute>
        ),
      },
      {
        path: RoutesEnum.STATUS_MESSAGE_MANAGEMENT,
        element: (
          <AdminRoute>
            <StatusMessageManagement />
          </AdminRoute>
        ),
      },
      {
        path: RoutesEnum.FEATURE_OVERVIEW,
        element: (
          <PrivateRoute>
            <FeatureOverview />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.HR_BOOSTER,
        element: (
          <PrivateRoute>
            <HrBooster />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.HR_BOOSTER_GENERATE_GLOBAL_PROFILE,
        element: (
          <PrivateRoute>
            <HrBoosterTextGenerationNew profileType={ProfileTypes.GLOBAL} />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.HR_BOOSTER_GENERATE_TAILORED_PROFILE,
        element: (
          <PrivateRoute>
            <HrBoosterTextGenerationNew profileType={ProfileTypes.TAILORED} />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.HR_BOOSTER_OPTIMIZE,
        element: (
          <PrivateRoute>
            <AugmentedWriting />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.HR_BOOSTER_DONE,
        element: (
          <PrivateRoute>
            <AugmentedWritingComplete />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.HR_BOOSTER_AUTO_FUNNEL,
        element: <Navigate to={{ pathname: RoutesEnum.RECRUITING_BOOSTER_CO_PILOT_PROJECTS }} />,
      },
      {
        path: RoutesEnum.LEAD_BOOSTER_AUTO_FUNNEL,
        element: <Navigate to={{ pathname: RoutesEnum.LEAD_BOOSTER_CO_PILOT_PROJECTS }} />,
      },
      {
        path: RoutesEnum.EXTERNAL_LINKEDIN_ACCOUNTS,
        element: (
          <ErrorBoundary>
            <LinkedinAccountExternal />
          </ErrorBoundary>
        ),
      },
      {
        path: RoutesEnum.EXTERNAL_CALL_TRANSCRIPTS,
        element: (
          <PrivateRoute>
            <CallTranscriptUI />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.LEAD_BOOSTER,
        element: (
          <PrivateRoute>
            <LeadBooster />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.LEAD_BOOSTER_GENERATE_GLOBAL_PROFILE,
        element: (
          <PrivateRoute>
            <LeadBoosterTextGeneration profileType={ProfileTypes.GLOBAL} />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.LEAD_BOOSTER_GENERATE_TAILORED_PROFILE,
        element: (
          <PrivateRoute>
            <LeadBoosterTextGeneration profileType={ProfileTypes.TAILORED} />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.LEAD_BOOSTER_OPTIMIZE,
        element: (
          <PrivateRoute>
            <AugmentedWriting />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.LEAD_BOOSTER_DONE,
        element: (
          <PrivateRoute>
            <AugmentedWritingComplete />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.PERSONA_GENERATOR,
        element: (
          <PrivateRoute>
            <ProfileGenerator />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.URL_FINDER,
        element: (
          <PrivateRoute>
            <LinkedinProfileFinder />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.MOTIVE_TESTS,
        element: (
          <PrivateRoute>
            <MotiveTests />
          </PrivateRoute>
        ),
      },
      {
        // Optionaler Reiter (/account/users …): eine Route, damit der Wechsel die Seite nicht neu aufbaut.
        path: `${RoutesEnum.ACCOUNT}/:tab?`,
        element: (
          <PrivateRoute>
            <AccountManagement />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.UPGRADE,
        element: (
          <PrivateRoute>
            <Payment />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.CONTACT_MANAGEMENT_OVERVIEW,
        element: (
          <PrivateRoute>
            <ContactManagement />
          </PrivateRoute>
        ),
      },
      {
        path: `${RoutesEnum.CONTACT_MANAGEMENT_CONTACT}/:contactId`,
        element: (
          <PrivateRoute>
            <ContactDetails />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.RECRUITING_BOOSTER_CO_PILOT,
        element: (
          <PrivateRoute>
            <CoPilotRootLayout productGroup={ProductGroups.HR_BOOSTER} />
          </PrivateRoute>
        ),
        children: getCoPilotChildRoutes(ProductGroups.HR_BOOSTER),
      },
      {
        path: RoutesEnum.LEAD_BOOSTER_CO_PILOT,
        element: (
          <PrivateRoute>
            <CoPilotRootLayout productGroup={ProductGroups.LEAD_BOOSTER} />
          </PrivateRoute>
        ),
        children: getCoPilotChildRoutes(ProductGroups.LEAD_BOOSTER),
      },
      {
        path: '/webmetics', // or use RoutesEnum.WEBMETICS if you've defined it in your enums file
        element: (
          <PrivateRoute>
            <WebmeticsLanding />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.LEAD_LIST_RESEARCH,
        element: (
          <AdminRoute>
            <LeadListResearchGenerate />
          </AdminRoute>
        ),
      },
      {
        path: RoutesEnum.LEAD_LIST_STORAGE,
        element: (
          <AdminRoute>
            <LeadListStorage />
          </AdminRoute>
        ),
      },
      {
        path: `${RoutesEnum.LEAD_LIST_STORAGE}/:id`,
        element: (
          <AdminRoute>
            <LeadListStorageDetail />
          </AdminRoute>
        ),
      },
      {
        path: '/use-case-store',
        element: (
          <AdminRoute>
            <UseCaseStore />
          </AdminRoute>
        ),
      },
      {
        path: RoutesEnum.PIPELINE,
        element: (
          <PrivateRoute>
            <Pipeline />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.AI_CHAT,
        element: (
          <PrivateRoute>
            <AIChatPage />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.FEEDBACK_TICKET,
        element: (
          <PrivateRoute>
            <FeedbackTicketPage />
          </PrivateRoute>
        ),
      },
      {
        path: '*',
        element: authenticated ? (
          <PrivateRoute>
            <PageNotFound />
          </PrivateRoute>
        ) : (
          // Remember the destination instead of dropping it: an unknown path here is often a
          // perfectly valid one the visitor simply is not logged in for yet.
          <RedirectToLogin />
        ),
      },
    ],
    {
      basename: '/dashboard',
    }
  );

  return (
    <Elements stripe={stripePromise}>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={adapterLocale}>
        <CacheProvider value={muiCache}>
          <TssCacheProvider value={tssCache}>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <GlobalScrollbarStyles />
              <ReactFlowProvider>
                <Suspense fallback={<PageLoading />}>
                  <RouterProvider router={router} />
                </Suspense>
              </ReactFlowProvider>
              {/* Nach dem kostenlosen Test ohne Kauf: Kampagnen ruhen, Modal auf jeder Seite (DEV-1173) */}
              {authenticated && <PaymentRequiredModal />}
            </ThemeProvider>
          </TssCacheProvider>
        </CacheProvider>
      </LocalizationProvider>
    </Elements>
  );
};

export default App;
